import { Popconfirm, Space, Switch } from 'antd';
import { useStrapiQuery } from 'hooks/useStrapiQuery';
import { useMemo, useState, type VFC } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { notifyError } from 'utils/notifications';
import { formatQuery, QueryBuilder, type RuleGroupType } from 'components/QueryBuilder';
import { useUpdateHardwarePackage } from 'api/hooks/hardware-package/useUpdate';
import { useThirdParties } from 'api/hooks/third-parties/useFind';
import { Button, Card, PageHeader } from 'components';
import { GET_HARDWARE_PACKAGE_DATA, type HardwarePackageData } from './queries/getHardwarePackageData';
import { DEFAULT_QUERY } from './utils';
import { getFields } from './fields';

interface QueryBuilderPageRouteState {
  inventoryMeterId?: number;
}

export const QueryBuilderPage: VFC = () => {
  const history = useHistory<QueryBuilderPageRouteState>();
  const { params } = useRouteMatch<{ id?: string }>();

  const [query, setQuery] = useState<RuleGroupType>(DEFAULT_QUERY);
  const [showQuery, setShowQuery] = useState(false);

  const { data: thirdPartiesData } = useThirdParties({ fields: ['id', 'name'] });
  const { data: thirdParties } = thirdPartiesData || {};

  const fields = useMemo(() => getFields(query, thirdParties), [query, thirdParties]);

  const navigateBack = () => {
    history.goBack();
  };

  const { data, loading: isLoading } = useStrapiQuery<HardwarePackageData, { id: number }>(GET_HARDWARE_PACKAGE_DATA, {
    variables: {
      id: Number(params.id),
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    onCompleted: ({
      hardwarePackage: {
        data: { query: hwPackageQuery },
      },
    }) => {
      if (hwPackageQuery) {
        setQuery(hwPackageQuery as unknown as RuleGroupType);
      }
    },
    onError(error) {
      notifyError(error, {
        message: `Action failed. Please try again.`,
      });

      navigateBack();
    },
  });

  const {
    hardwarePackage: { data: hardwarePackageData },
  } = data || { hardwarePackage: {} };
  const { mutateAsync: updateHardwarePackage, isLoading: isUpdating } = useUpdateHardwarePackage();

  const handleSave = async () => {
    await updateHardwarePackage({
      id: Number(params.id),
      values: {
        ...hardwarePackageData,
        query: query as unknown as Record<string, unknown>,
      },
    });
  };

  const handleResetButtonClick = () => {
    if (!hardwarePackageData?.query) {
      setQuery(hardwarePackageData?.query as unknown as RuleGroupType);
    } else {
      setQuery(DEFAULT_QUERY);
    }
  };

  const handleBack = () => {
    navigateBack();
  };

  return (
    <Space className="w-100" direction="vertical">
      <PageHeader ghost={false} title={hardwarePackageData?.name} onBack={handleBack} />
      <Card
        extra={
          <Space>
            <Switch
              defaultChecked
              checked={showQuery}
              checkedChildren="Hide query"
              unCheckedChildren="Show query"
              onChange={() => setShowQuery(!showQuery)}
            />
            <Popconfirm
              cancelText="Cancel"
              okText="Reset"
              title={
                <Space>
                  <span>Are you sure you want to reset all unsaved changes?</span>
                </Space>
              }
              zIndex={99999}
              onConfirm={handleResetButtonClick}
            >
              <Button>Reset</Button>
            </Popconfirm>
            <Button loading={isUpdating} type="primary" onClick={handleSave}>
              Save
            </Button>
          </Space>
        }
        loading={isLoading}
      >
        <QueryBuilder
          listsAsArrays
          showCloneButtons
          controlClassnames={{ queryBuilder: 'queryBuilder-branches' }}
          fields={fields}
          query={query}
          onQueryChange={q => setQuery(q)}
        />
      </Card>
      {showQuery && (
        <Card title="Query">
          <pre>
            <code>{formatQuery(query, 'json')}</code>
          </pre>
        </Card>
      )}
    </Space>
  );
};
